/**
 * M4 (Drip Campaigns) — analytics panel.
 *
 * Renders the campaign-level funnel + per-step breakdown + drop-off
 * buckets returned by GET /api/drip-campaigns/:id/analytics. Uses recharts
 * bar charts (already a dependency via shadcn/chart.tsx).
 *
 * The panel is intentionally read-only: edits happen in the steps tab.
 * If the analytics endpoint returns nothing useful (e.g. the campaign
 * hasn't sent yet), we render an empty state instead of an empty chart.
 */
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from "recharts";
import { Loader2, TrendingUp } from "lucide-react";

interface FunnelCounts {
  enrolled: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  replied: number;
  booked: number;
  failed: number;
  suppressed: number;
  skipped: number;
}

interface PerStepCounts {
  stepId: string;
  stepOrder: number;
  sent: number;
  opened: number;
  clicked: number;
  failed: number;
  suppressed: number;
  skipped: number;
}

interface CampaignAnalytics {
  funnel: FunnelCounts;
  perStep: PerStepCounts[];
  dropOff: { reason: string; count: number }[];
}

const FUNNEL_COLORS = [
  "hsl(217 91% 60%)", // enrolled
  "hsl(217 91% 55%)", // sent
  "hsl(217 91% 50%)", // delivered
  "hsl(142 70% 45%)", // opened
  "hsl(142 70% 40%)", // clicked
  "hsl(38 92% 50%)", // replied
  "hsl(280 80% 55%)", // booked
];

export function DripAnalyticsPanel({ campaignId }: { campaignId: string }) {
  const { data, isLoading } = useQuery<CampaignAnalytics>({
    queryKey: ["/api/drip-campaigns", campaignId, "analytics"],
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <TrendingUp className="h-10 w-10 mx-auto mb-2 opacity-40" />
        <p data-testid="text-analytics-empty">No analytics yet — send the first step to populate this dashboard.</p>
      </div>
    );
  }

  const { funnel, perStep, dropOff } = data;
  const sent = funnel.sent || 0;
  const pct = (n: number) => (sent > 0 ? Math.round((n / sent) * 100) : 0);

  const funnelData = [
    { stage: "Enrolled", value: funnel.enrolled },
    { stage: "Sent", value: funnel.sent },
    { stage: "Delivered", value: funnel.delivered },
    { stage: "Opened", value: funnel.opened },
    { stage: "Clicked", value: funnel.clicked },
    { stage: "Replied", value: funnel.replied },
    { stage: "Booked", value: funnel.booked },
  ];

  const perStepData = perStep.map((s) => ({
    label: `Step ${s.stepOrder}`,
    Sent: s.sent,
    Opened: s.opened,
    Clicked: s.clicked,
    Failed: s.failed,
    Skipped: s.skipped,
  }));

  return (
    <div className="space-y-6" data-testid="panel-drip-analytics">
      {/* Top-line counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: "Enrolled", value: funnel.enrolled, sub: "" },
          { label: "Sent", value: funnel.sent, sub: "" },
          { label: "Opened", value: funnel.opened, sub: `${pct(funnel.opened)}%` },
          { label: "Clicked", value: funnel.clicked, sub: `${pct(funnel.clicked)}%` },
          { label: "Replied", value: funnel.replied, sub: `${pct(funnel.replied)}%` },
          { label: "Booked", value: funnel.booked, sub: `${pct(funnel.booked)}%` },
          { label: "Failed", value: funnel.failed, sub: "" },
        ].map((m) => (
          <Card key={m.label}>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">{m.label}</div>
              <div className="text-2xl font-semibold tabular-nums" data-testid={`metric-${m.label.toLowerCase()}`}>
                {m.value}
              </div>
              {m.sub && <div className="text-xs text-muted-foreground">{m.sub}</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Funnel chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Funnel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {funnelData.map((_, idx) => (
                    <Cell key={idx} fill={FUNNEL_COLORS[idx % FUNNEL_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Per-step breakdown */}
      {perStepData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Per-step performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={perStepData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="Sent" fill="hsl(217 91% 55%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Opened" fill="hsl(142 70% 45%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Clicked" fill="hsl(142 70% 35%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Failed" fill="hsl(0 70% 50%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Skipped" fill="hsl(0 0% 60%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drop-off buckets */}
      {dropOff.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Drop-off</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {dropOff.map((b) => (
                <div
                  key={b.reason}
                  className="rounded-md border px-3 py-2 text-sm"
                  data-testid={`dropoff-${b.reason}`}
                >
                  <span className="text-muted-foreground capitalize">{b.reason}: </span>
                  <span className="font-semibold tabular-nums">{b.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface LeaderboardRow {
  id: string;
  name: string;
  enrolled: number;
  sent: number;
  replied: number;
  booked: number;
  replyRate: number;
  bookRate: number;
}

/**
 * Workspace-wide top-campaigns leaderboard, rendered on the drip tab landing.
 */
export function DripLeaderboardCard() {
  const { data, isLoading } = useQuery<LeaderboardRow[]>({
    queryKey: ["/api/drip-analytics/leaderboard"],
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) return null;

  return (
    <Card data-testid="card-drip-leaderboard">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Top campaigns
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {data.map((row, idx) => (
            <div
              key={row.id}
              className="flex items-center justify-between px-4 py-2 text-sm"
              data-testid={`leaderboard-row-${row.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-muted-foreground tabular-nums w-5 text-right">
                  {idx + 1}
                </span>
                <span className="truncate font-medium">{row.name}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground tabular-nums">
                <span>{row.sent} sent</span>
                <span>{Math.round(row.replyRate * 100)}% reply</span>
                <span>{Math.round(row.bookRate * 100)}% book</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
